import React, { useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import './JsonViewer.css';

type Props = {
  /**
   * Le contenu JSON à afficher. Peut être un objet, un tableau ou une chaîne JSON.
   */
  json: any;
  
  /**
   * Hauteur maximale du conteneur. Par défaut: '300px'.
   */
  maxHeight?: string;
  
  /**
   * Indique si le JSON doit être indenté. Par défaut: true.
   */
  pretty?: boolean;
  
  /**
   * Nombre d'espaces pour l'indentation. Par défaut: 2.
   */
  indent?: number;
  
  /**
   * Styles CSS supplémentaires à appliquer au conteneur.
   */
  sx?: React.CSSProperties;
}

/**
 * Utilitaire pour coloriser du JSON
 */
const jsonColorizer = {
  /**
   * Formate et colorise un objet JSON
   */
  prettyPrint: function(json: any, spaces = 2) {
    try {
      // Si json est une chaîne, essayer de la parser
      let jsonObj = json;
      if (typeof json === 'string') {
        try {
          jsonObj = JSON.parse(json);
        } catch (e) {
          // Si le parsing échoue, utiliser la chaîne telle quelle
          jsonObj = json;
        }
      }

      const text = JSON.stringify(jsonObj, null, spaces);
      if (!text) return '';

      const escaped = text
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');

      return escaped.replace(
        /("(\\u[a-zA-Z0-9]{4}|\\[^u]|[^\\"])*"(\s*:)?|\b(true|false|null)\b|-?\d+(?:\.\d*)?(?:[eE][+-]?\d+)?)/g,
        (match) => {
          let cls = 'json-number';
          if (/^"/.test(match)) {
            cls = /:$/.test(match) ? 'json-key' : 'json-string';
          } else if (/true|false/.test(match)) {
            cls = 'json-boolean';
          } else if (/null/.test(match)) {
            cls = 'json-null';
          }
          return `<span class='${cls}'>${match}</span>`;
        }
      );
    } catch (err) {
      return `Erreur de formatage JSON: ${err instanceof Error ? err.message : String(err)}`;
    }
  }
};

/**
 * Composant pour afficher du JSON formaté et colorisé
 */
function JsonViewer({
  json,
  maxHeight = '300px',
  pretty = true,
  indent = 2,
  sx = {}
}: Props) {
  const [formattedJson, setFormattedJson] = useState<string>('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    try {
      const colorized = jsonColorizer.prettyPrint(json, pretty ? indent : 0);
      setFormattedJson(colorized);
      setError(null);
    } catch (err) {
      setError(`Erreur de formatage JSON: ${err instanceof Error ? err.message : String(err)}`);
      setFormattedJson('');
    }
  }, [json, pretty, indent]);

  return (
    <Box 
      sx={{ 
        fontFamily: 'monospace',
        maxHeight: maxHeight,
        overflow: 'auto',
        padding: 1,
        backgroundColor: '#1a1a1a',
        borderRadius: 1,
        border: '1px solid #e0e0e0',
        ...sx
      }}
    >
      {error ? (
        <Typography color="error">{error}</Typography>
      ) : (
        <pre 
          style={{ margin: 0, color: 'whitesmoke' }} 
          dangerouslySetInnerHTML={{ __html: formattedJson }}
          className="json-viewer"
        />
      )}

      {/* Les styles sont injectés via sx prop */}
    </Box>
  );
}

export default JsonViewer;